<template>
  <div>
    <div @click="showModal()">
      <slot></slot>
    </div>

    <HeadlessTransitionRoot as="template" :show="open">
      <HeadlessDialog as="div" class="relative z-50" @close="open = false">
        <HeadlessTransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
          leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </HeadlessTransitionChild>

        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <HeadlessTransitionChild as="template" enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <HeadlessDialogPanel
                class="relative transform overflow-hidden rounded-lg bg-woodsmoke-850 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm">
                <div class="w-full flex justify-between items-center border-b border-b-white/10 p-4">
                  <div class="flex justify-center items-center space-x-2">
                    <h1 class="text-lg text-turquoise-500">View Listing</h1>
                    <OTCGuide start="buy">
                      <button
                        class="w-6 h-6 bg-turquoise-500/30 flex justify-center items-center rounded-full btn btn-circle min-h-0 m-0 p-0 border-none">
                        <Icon class="text-turquoise-500 text-xl" name="bi:question"></Icon>
                      </button>
                    </OTCGuide>
                  </div>
                  <button @click="closeModal()" class="btn btn-sm bg-white/5 capitalize text-red-500">close</button>
                </div>

                <div class="w-full px-4 mt-4 flex justify-between items-center">
                  <div class="flex justify-start items-center space-x-4">
                    <div
                      class="w-12 h-12 bg-white/5 rounded-md border border-white/10 flex-shrink-0 flex justify-center items-center">
                      <img class="w-10 h-10 rounded-md border border-white/20" :src="project.logo" alt="" />
                    </div>
                    <div>
                      <p class="text-turquoise-500 text-sm">{{ project.name }}</p>
                      <p class="text-lg text-white">{{ pool.name }}</p>
                    </div>
                  </div>

                  <div class="text-right">
                    <p class="text-sm text-woodsmoke-300">owner</p>
                    <p class="">{{ listing.owner.substring(0, 10) }}...</p>
                  </div>
                </div>

                <div class="px-4">
                  <OTCSimulationDiagramBuyer :price="listing.price" :markup="(listing.price - listing.allocation)"
                    :allocation="listing.allocation" />
                  <p class="text-xs mt-2">Simulate your effective contribution and refund</p>
                </div>

                <div class="w-full grid grid-cols-5 px-4 mt-4 border-y border-y-white/10 py-2">

                  <div class="col-span-3 text-sm text-white">Price</div>
                  <div class="col-span-2 text-right">{{ Number(listing.price).toFixed(2) }} {{ listing.currency }}</div>

                  <div class="col-span-3 text-sm text-white">Allocation</div>
                  <div class="col-span-2 text-right">{{ Number(listing.allocation).toFixed(2) }} {{ listing.currency }}
                  </div>

                  <div class="col-span-3 text-sm text-white">Markup</div>
                  <div class="col-span-2 text-right">{{ Number(listing.price - listing.allocation).toFixed(2) }} {{
                    listing.currency }} ({{ Number((listing.price - listing.allocation) / listing.allocation *
    100).toFixed(2) }}%)</div>

                  <template v-if="isPrivate">
                    <div class="col-span-3 text-sm text-white">Private Fee</div>
                    <div class="col-span-2 text-right">{{ Number(listing.allocation * 0.03).toFixed(2) }} {{
                      listing.currency }} <span class="text-sm"> (3% x {{ Number(listing.allocation).toFixed(2) }}{{
    listing.currency }})</span></div>
                  </template>
                </div>

                <div class="mt-8 px-4 mb-8">
                  <p class="text-sm text-white">By proceeding, you understand that:</p>
                  <p class="text-sm">1. The final effective contribution is subject to change based on the project's
                    contribution status.</p>
                  <p class="text-sm">2. Your effective contribution is likely to be less than the listing price.</p>
                  <p class="text-sm">3. Contribution amount is calculated base on allocation amount. </p>
                  <p v-if="isPrivate" class="text-sm">4. Unused allocation, markup and private fee are subjected to
                    refund.</p>
                  <p v-if="!isPrivate" class="text-sm">4. Unused allocation, markup and are subjected to refund.</p>
                  <p class="text-sm">5. Contribution is made instantly after purchase.</p>
                </div>

                <div class="w-full mt-4 flex justify-between items-center px-4 pb-4">
                  <div>
                    <p class="text-sm">Listing Price</p>
                    <p class="text-lg text-white">{{ Number(listing.price).toFixed(2) }} {{ listing.currency }}</p>
                  </div>

                  <button v-if="shouldShowBuyButton(listing)" @click="confirmBuy()"
                    class="btn bg-turquoise-500 text-turquoise-1000 min-h-0 h-auto p-3 m-0 hover:text-turquoise-500">Proceed
                    to Buy</button>
                </div>
              </HeadlessDialogPanel>
            </HeadlessTransitionChild>
          </div>
        </div>
      </HeadlessDialog>
    </HeadlessTransitionRoot>
  </div>
</template>

<script setup>
import { useUserStore } from "~/stores/user.js";
const userStore = useUserStore();

function shouldShowBuyButton(listing) {
  if (!listing.is_owner) {
    if (!listing.bought && !listing.unlisted && listing.active)
      return true
  }
  return false
}

const prop = defineProps(['listing', 'pool', 'project'])
const { listing, pool, project } = toRefs(prop)
import { getAccount, readContract, writeContract, waitForTransactionReceipt } from "@wagmi/core";
// import { config } from "~/wagmiConfig.js";
import { erc20Abi, parseEther } from "viem";

const { $address, $accessToken } = useNuxtApp()


const open = ref(false);

const isPrivate = computed(() => {
  return true
})

const showModal = () => {
  open.value = true;
};

const closeModal = () => {
  open.value = false;
};

const toast = useToast();
const _toast = (param) => {
  toast.add({
    ...param,
    ui: {
      strategy: "override",
      icon: {
        base: "flex-shrink-0 w-5 h-5 mt-0.5",
        color: "text-{color}-500 dark:text-{color}-400",
      },
      background: "bg-woodsmoke-800/50 backdrop-blur-md",
      container: "border border-turquoise-500/20 relative overflow-hidden",
      title: "text-turquoise-500 text-base",
      description: "text-white text-sm",
    },
  });
};

const estimateETHFee = async (address) => {
  try {
    const _fee = await readContract(config, {
      abi: [{
        "inputs": [],
        "name": "getCurrentFee",
        "outputs": [
          {
            "internalType": "uint256",
            "name": "",
            "type": "uint256"
          }
        ],
        "stateMutability": "view",
        "type": "function"
      }],
      address,
      functionName: "getCurrentFee",
    });

    const etherFee = Number(_fee)
    // const scaleFactor = 10000;
    // const roundedNumber = Math.ceil(etherFee * scaleFactor) / scaleFactor;
    // return roundedNumber;
    return etherFee
  } catch (error) {
    return 0.005;
  }
};

async function confirmBuy() {

  userStore.pauseUI = true;

  $fetch(`/api/otc/list/${listing.value.id}/buy`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${$accessToken.value}`,
    },
    method: 'POST',
  })
    .then(async response => {
      const ethFee = await estimateETHFee(response.list.otc_ca);

      // let _account = getAccount(config);

      let _allowance = await readContract(config, {
        abi: erc20Abi,
        address: response.list.currency_ca,
        functionName: "allowance",
        args: [$address.value, response.list.otc_ca],
      });

      const totalPriceWei = parseEther(response.list.total_price_eth)
      if (_allowance < totalPriceWei) {
        let _approveTx = await writeContract(config, {
          abi: erc20Abi,
          address: response.list.currency_ca,
          functionName: "approve",
          args: [response.list.otc_ca, totalPriceWei],
        });

        if (_approveTx) {

          try {
            let approveResult = await waitForTransactionReceipt(config, {
              hash: _approveTx,
              confirmations: 2,
            });

            _toast({
              id: "approve",
              title: "Approved!",
              description: "Your approval has been successful.",
              timeout: 5000,
            });
          } catch (err) {
            console.log(err?.shortMessage)
            _toast({
              id: "approve_error",
              title: "Approval Failed!",
              description: `${err?.shortMessage}`,
              timeout: 5000,
            });

          }
        }

      }

      const res = await writeContract(config, {
        address: response.list.otc_ca,
        functionName: "otcContribute",
        args: [response.data, response.signature],
        abi: [{
          "inputs": [
            {
              "internalType": "bytes",
              "name": "data",
              "type": "bytes"
            },
            {
              "internalType": "bytes",
              "name": "signature",
              "type": "bytes"
            }
          ],
          "name": "otcContribute",
          "outputs": [],
          "stateMutability": "payable",
          "type": "function"
        }],
        value: ethFee
      })

      const receipt = await waitForTransactionReceipt(config, {
        hash: res,
        confirmations: 2,
      });

      _toast({
        id: "buy_psp",
        title: "Success!",
        description: "Successfully Bought",
        timeout: 5000,
      });
      
      closeModal();

      userStore.pauseUI = false;

    })
    .catch((error) => {
      alert(error.message);

      userStore.pauseUI = false;

    })


}
</script>

<style></style>
